/**
 * DevFieldPlaygroundScreen
 *
 * Visual exercise of every Phase 2.2 primitive in every state of the
 * Phase 1.0 component matrix — idle / focused / error / disabled / loading.
 *
 * Lives behind the existing dev-tools gate (`__DEV__` or
 * `EXPO_PUBLIC_DEV_TOOLS=true`). Doesn't ship to production builds.
 *
 * Each section mounts a single primitive multiple times so the matrix is
 * eyeball-checkable without opening 7 modals across the app. State is
 * local to each mount so callers can interact with one field without
 * yanking the others.
 */

import { useState } from "react";
import { Pressable, ScrollView, View } from "react-native";
import { useNavigation } from "@react-navigation/native";
import {
  ChevronLeft,
  Sun,
  Moon,
  Sunset,
  CloudMoon,
  Inbox,
  Calendar,
  MapPin,
  Building2,
} from "lucide-react-native";
import { MoonText } from "@louise/moon-ui/primitives/MoonText";
import {
  MoonFormField,
  MoonNumericField,
  MoonCurrencyInput,
  MoonSelectField,
  MoonDateField,
  MoonPhoneField,
  MoonStepper,
  MoonToggle,
  MoonFilterChip,
  MoonDivider,
  MoonCheckbox,
  MoonRadio,
  MoonStatBlock,
  MoonSettingRow,
  MoonAvatarNameCard,
  MoonEmptyState,
  MoonSearchInput,
  MoonDetailRow,
  DialogPrimaryButton,
  MoonPressable,
} from "@louise/moon-ui";
import { MoonTimeField } from "@louise/moon-ui/primitives/MoonTimeField";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  colors,
  spacing,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <View style={{ gap: spacing.md, marginTop: spacing.xl }}>
      <View>
        <MoonText variant="section-header" color="textMid">
          {title}
        </MoonText>
        <MoonText variant="body-sm" color="textLight" style={{ marginTop: 2 }}>
          {description}
        </MoonText>
      </View>
      <View style={{ gap: spacing.md }}>{children}</View>
    </View>
  );
}

const FREQUENCY_OPTIONS = [
  {
    label: "Daily",
    value: "daily",
    description: "Take every day at the same time",
  },
  { label: "Weekly", value: "weekly" },
  { label: "Monthly", value: "monthly" },
  { label: "As needed", value: "prn", description: "Only when symptoms occur" },
];

export default function DevFieldPlaygroundScreen() {
  const navigation = useNavigation();

  // Numeric / currency / time / select state for the live mounts. The
  // matrix variants (error / disabled / loading) below use their own
  // hard-coded values so the demo state stays clear.
  const [count, setCount] = useState<number | null>(12);
  const [cost, setCost] = useState<number | null>(1450.5);
  const [time, setTime] = useState<string>("09:30");
  const [frequency, setFrequency] = useState<string | null>("daily");
  const [date, setDate] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  const [steps, setSteps] = useState<number>(3);
  const [toggleOn, setToggleOn] = useState<boolean>(true);
  const [chipSelections, setChipSelections] = useState<Record<string, boolean>>(
    { daily: true, weekly: false, monthly: false, prn: false },
  );
  const [checkboxes, setCheckboxes] = useState<Record<string, boolean>>({
    a: true,
    b: false,
    c: false,
  });
  const [radio, setRadio] = useState<string>("a");
  const [search, setSearch] = useState<string>("");
  const [searchLoading, setSearchLoading] = useState<string>("Lupron");

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          padding: spacing.lg,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 96,
          maxWidth: 480,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        <Pressable
          onPress={() => navigation.goBack()}
          hitSlop={8}
          style={{ flexDirection: "row", alignItems: "center", gap: 4 }}
        >
          <ChevronLeft size={18} color={colors.warm} strokeWidth={1.5} />
          <MoonText variant="body-sm" color="warm">
            Back
          </MoonText>
        </Pressable>

        <View style={{ marginTop: spacing.lg }}>
          <MoonText variant="display">Field Playground</MoonText>
          <MoonText
            variant="body-sm"
            color="textLight"
            style={{ marginTop: 4 }}
          >
            Phase 2.2 input primitives across every state in the Phase 1.0
            component matrix.
          </MoonText>
        </View>

        <Section
          title="MoonNumericField"
          description="Integer / decimal input with parsing. value is number | null; min / max clamp on blur."
        >
          <MoonNumericField
            label="Idle — type a number"
            value={count}
            onChange={setCount}
            placeholder="0"
            hint="Live state below the demo"
          />
          <MoonNumericField
            label="With suffix"
            value={count}
            onChange={setCount}
            suffix="eggs"
          />
          <MoonNumericField
            label="Decimals (2)"
            value={1.5}
            onChange={() => {}}
            decimals={2}
            hint="Buffer keeps intermediate states like '12.'"
          />
          <MoonNumericField
            label="With error"
            value={42}
            onChange={() => {}}
            error="Must be between 1 and 30"
          />
          <MoonNumericField
            label="Disabled"
            value={7}
            onChange={() => {}}
            disabled
          />
          <MoonNumericField
            label="Loading"
            value={null}
            onChange={() => {}}
            loading
          />
        </Section>

        <Section
          title="MoonCurrencyInput"
          description="Wraps MoonNumericField with decimals=2 and a currency prefix."
        >
          <MoonCurrencyInput
            label="Idle"
            value={cost}
            onChange={setCost}
            placeholder="0.00"
          />
          <MoonCurrencyInput
            label="Different currency"
            value={89.99}
            onChange={() => {}}
            currency="€"
          />
          <MoonCurrencyInput
            label="With error"
            value={-50}
            onChange={() => {}}
            error="Amount cannot be negative"
          />
          <MoonCurrencyInput
            label="Disabled"
            value={2400}
            onChange={() => {}}
            disabled
          />
        </Section>

        <Section
          title="MoonTimeField"
          description="HH:MM (24-hour) entry. Validates via isValidTime; clamps via clampTime on blur."
        >
          <MoonTimeField
            label="Idle — try typing '25:99'"
            value={time}
            onChange={setTime}
            hint="On blur it clamps to 23:59"
          />
          <MoonTimeField
            label="With error"
            value="14:00"
            onChange={() => {}}
            error="Must be after the trigger shot time"
          />
          <MoonTimeField
            label="Disabled"
            value="08:00"
            onChange={() => {}}
            disabled
          />
        </Section>

        <Section
          title="MoonSelectField"
          description="Modal-based single-select. Generic over the option value type. Tap opens a centered Dialog with the options."
        >
          <MoonSelectField
            label="Frequency"
            options={FREQUENCY_OPTIONS}
            value={frequency}
            onChange={setFrequency}
            pickerTitle="How often?"
          />
          <MoonSelectField
            label="Empty (no selection)"
            options={FREQUENCY_OPTIONS}
            value={null}
            onChange={() => {}}
            placeholder="Pick a cadence…"
          />
          <MoonSelectField
            label="With error"
            options={FREQUENCY_OPTIONS}
            value={null}
            onChange={() => {}}
            error="Frequency is required"
          />
          <MoonSelectField
            label="Disabled"
            options={FREQUENCY_OPTIONS}
            value="weekly"
            onChange={() => {}}
            disabled
          />
        </Section>

        <Section
          title="MoonDateField"
          description="Wraps MoonDatePicker in MoonFormField + MoonFieldInput chrome. value is YYYY-MM-DD or empty string."
        >
          <MoonDateField
            label="Idle"
            value={date}
            onChange={setDate}
            placeholder="Pick a date"
            hint="Tap to open the year / month / day picker"
          />
          <MoonDateField
            label="With error"
            value=""
            onChange={() => {}}
            error="Date is required"
          />
          <MoonDateField
            label="Disabled"
            value="2026-05-01"
            onChange={() => {}}
            disabled
          />
        </Section>

        <Section
          title="MoonPhoneField"
          description="Inline phone input — no modal. Type-as-you-go libphonenumber-js formatting; the dial code (+1, +33, etc.) shows as a static prefix. Pasting an international number with a leading + re-detects the country."
        >
          <MoonPhoneField label="Phone" value={phone} onChange={setPhone} />
          <MoonPhoneField
            label="With error"
            value="+155"
            onChange={() => {}}
            error="Not a valid number"
          />
          <MoonPhoneField
            label="Disabled"
            value="+15551234567"
            onChange={() => {}}
            disabled
          />
        </Section>

        <Section
          title="MoonStepper"
          description="Bare – N + counter. Audit 2.5. Compose inside MoonFormField for label/error chrome, or use inline next to a label like AddSpecimenModal does."
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <MoonText variant="body-sm" color="textMid">
              Live (inline label)
            </MoonText>
            <MoonStepper value={steps} onChange={setSteps} min={0} max={10} />
          </View>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <MoonText variant="body-sm" color="textMid">
              At min (0)
            </MoonText>
            <MoonStepper value={0} onChange={() => {}} min={0} max={5} />
          </View>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <MoonText variant="body-sm" color="textMid">
              At max (5)
            </MoonText>
            <MoonStepper value={5} onChange={() => {}} min={0} max={5} />
          </View>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <MoonText variant="body-sm" color="textMid">
              Disabled
            </MoonText>
            <MoonStepper value={3} onChange={() => {}} disabled />
          </View>
          <MoonFormField label="Inside MoonFormField" hint="Stacked label">
            <View style={{ paddingVertical: 4 }}>
              <MoonStepper value={steps} onChange={setSteps} min={0} max={20} />
            </View>
          </MoonFormField>
        </Section>

        <Section
          title="MoonToggle"
          description="Shared textured active track with a quick animated thumb."
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <MoonText variant="body-sm" color="textMid">
              Live (toggle me)
            </MoonText>
            <MoonToggle value={toggleOn} onChange={setToggleOn} />
          </View>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <MoonText variant="body-sm" color="textMid">
              Off
            </MoonText>
            <MoonToggle value={false} onChange={() => {}} />
          </View>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <MoonText variant="body-sm" color="textMid">
              On + disabled
            </MoonText>
            <MoonToggle value={true} onChange={() => {}} disabled />
          </View>
        </Section>

        <Section
          title="MoonFilterChip"
          description="Pill button for filter rows. Selected = warm tint + Check icon."
        >
          <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
            {FREQUENCY_OPTIONS.map((opt) => (
              <MoonFilterChip
                key={opt.value}
                label={opt.label}
                selected={!!chipSelections[opt.value]}
                onPress={() =>
                  setChipSelections((prev) => ({
                    ...prev,
                    [opt.value]: !prev[opt.value],
                  }))
                }
              />
            ))}
          </View>
          <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
            <MoonFilterChip
              label="No check icon"
              selected
              onPress={() => {}}
              showCheck={false}
            />
            <MoonFilterChip
              label="Disabled selected"
              selected
              onPress={() => {}}
              disabled
            />
            <MoonFilterChip
              label="Disabled idle"
              selected={false}
              onPress={() => {}}
              disabled
            />
          </View>
          <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
            <MoonFilterChip
              label="Morning"
              icon={Sun}
              radius={10}
              selected
              onPress={() => {}}
            />
            <MoonFilterChip
              label="Afternoon"
              icon={Sunset}
              radius={10}
              selected={false}
              onPress={() => {}}
            />
            <MoonFilterChip
              label="Evening"
              icon={Moon}
              radius={10}
              selected={false}
              onPress={() => {}}
            />
            <MoonFilterChip
              label="Night"
              icon={CloudMoon}
              radius={10}
              selected={false}
              onPress={() => {}}
            />
          </View>
        </Section>

        <Section
          title="MoonDivider"
          description="Hairline rule. Horizontal default; orientation='vertical' for column splits."
        >
          <View style={{ gap: 6 }}>
            <MoonText variant="body-sm" color="textMid">
              Default (colors.border)
            </MoonText>
            <MoonDivider />
            <MoonText
              variant="body-sm"
              color="textMid"
              style={{ marginTop: 6 }}
            >
              Inset 24px
            </MoonText>
            <MoonDivider inset={24} />
            <MoonText
              variant="body-sm"
              color="textMid"
              style={{ marginTop: 6 }}
            >
              Thicker (2px)
            </MoonText>
            <MoonDivider thickness={2} />
          </View>
          <View style={{ flexDirection: "row", alignItems: "center", gap: 12 }}>
            <MoonText variant="body-sm" color="textMid">
              Left
            </MoonText>
            <MoonDivider orientation="vertical" style={{ height: 24 }} />
            <MoonText variant="body-sm" color="textMid">
              Right (vertical split)
            </MoonText>
          </View>
        </Section>

        <Section
          title="MoonCheckbox"
          description="Square checkbox with optional inline label. Group selection lives in the parent."
        >
          <MoonCheckbox
            value={!!checkboxes.a}
            onChange={(v) => setCheckboxes((prev) => ({ ...prev, a: v }))}
            label="Live — toggle me"
          />
          <MoonCheckbox
            value={!!checkboxes.b}
            onChange={(v) => setCheckboxes((prev) => ({ ...prev, b: v }))}
            label="Unchecked"
          />
          <MoonCheckbox
            value={!!checkboxes.c}
            onChange={(v) => setCheckboxes((prev) => ({ ...prev, c: v }))}
            label="With error border"
            error
          />
          <MoonCheckbox value onChange={() => {}} label="Disabled" disabled />
          <MoonCheckbox value onChange={() => {}} />
        </Section>

        <Section
          title="MoonRadio"
          description="Circular single-select for inline option lists. Parent owns the selected value."
        >
          {FREQUENCY_OPTIONS.slice(0, 3).map((opt) => (
            <MoonRadio
              key={opt.value}
              value={radio === opt.value}
              onChange={() => setRadio(opt.value)}
              label={opt.label}
            />
          ))}
          <MoonRadio value onChange={() => {}} label="Disabled" disabled />
        </Section>

        <Section
          title="MoonStatBlock"
          description="Number + caption tile. Hue lives in the number; caption stays neutral."
        >
          <View style={{ flexDirection: "row", gap: spacing.lg }}>
            <MoonStatBlock value={12} label="Eggs frozen" />
            <MoonStatBlock
              value={3}
              label="Cycles"
              caption="+ 1 this quarter"
              accentColor={colors.warm}
            />
            <MoonStatBlock value="—" label="Loading" loading />
          </View>
          <View
            style={{
              padding: spacing.md,
              borderRadius: 12,
              backgroundColor: "rgba(255,255,255,0.4)",
            }}
          >
            <MoonStatBlock
              value="€1,450"
              label="Estimated remaining"
              caption="Center aligned"
              align="center"
              accentColor={colors.coral}
            />
          </View>
        </Section>

        <Section
          title="MoonSettingRow"
          description="Settings row: label + value/trailing + optional chevron. divider prop adds a top border."
        >
          <View
            style={{
              borderRadius: 12,
              backgroundColor: "rgba(255,255,255,0.5)",
              overflow: "hidden",
            }}
          >
            <MoonSettingRow label="Name" value="Onan Geko" onPress={() => {}} />
            <MoonSettingRow
              label="Email"
              value="extra@louise.life"
              onPress={() => {}}
              divider
            />
            <MoonSettingRow
              label="Notifications"
              trailing={<MoonToggle value={toggleOn} onChange={setToggleOn} />}
              chevron={false}
              divider
            />
            <MoonSettingRow
              label="Disabled row"
              value="Cannot tap"
              onPress={() => {}}
              disabled
              divider
            />
          </View>
        </Section>

        <Section
          title="MoonAvatarNameCard"
          description="Avatar + name + subtitle. Trailing slot for badges, optional chevron when onPress is set."
        >
          <View
            style={{
              borderRadius: 12,
              backgroundColor: "rgba(255,255,255,0.5)",
              overflow: "hidden",
            }}
          >
            <MoonAvatarNameCard
              name="Dr. Sarah Lee"
              subtitle="Reproductive Endocrinologist · Cornell"
              onPress={() => {}}
            />
            <MoonAvatarNameCard
              name="Jamie Park"
              subtitle="Partner"
              trailing={
                <View
                  style={{
                    backgroundColor: `${colors.warm}18`,
                    paddingHorizontal: 6,
                    paddingVertical: 1,
                    borderRadius: 4,
                  }}
                >
                  <MoonText
                    style={{
                      fontSize: 9,
                      fontWeight: "600",
                      color: colors.warm,
                    }}
                  >
                    Primary
                  </MoonText>
                </View>
              }
            />
            <MoonAvatarNameCard name="MJ" subtitle="Initials fallback" />
            <MoonAvatarNameCard
              name="Disabled User"
              subtitle="Cannot tap"
              onPress={() => {}}
              disabled
            />
          </View>
        </Section>

        <Section
          title="MoonEmptyState"
          description="Default = premium orb (icon stroke through GradientIcon). Pass iconOrb={false} for inline list-empty placeholders where a faded one-liner reads better."
        >
          <View
            style={{
              borderRadius: 12,
              backgroundColor: "rgba(255,255,255,0.5)",
              overflow: "hidden",
            }}
          >
            <MoonEmptyState
              icon={Calendar}
              accentColor={colors.warm}
              title="No specimens yet"
              subtitle="After your retrieval or freezing procedure, your specimens will appear here with their details and storage location."
              action={
                <DialogPrimaryButton label="Add specimen" onPress={() => {}} />
              }
            />
          </View>
          <View
            style={{
              borderRadius: 12,
              backgroundColor: "rgba(255,255,255,0.5)",
              overflow: "hidden",
            }}
          >
            <MoonEmptyState
              icon={Inbox}
              iconOrb={false}
              title="No appointments yet. Tap “Add appointment” to log your next visit."
            />
          </View>
        </Section>

        <Section
          title="MoonSearchInput"
          description="Search affordance with magnifying glass + optional trailing clear (X) or loading spinner."
        >
          <MoonSearchInput
            value={search}
            onChangeText={setSearch}
            onClear={() => setSearch("")}
            placeholder="Search clinics, medications…"
          />
          <MoonSearchInput
            value={searchLoading}
            onChangeText={setSearchLoading}
            onClear={() => setSearchLoading("")}
            loading
            placeholder="Search loading state"
          />
          <MoonSearchInput
            value=""
            onChangeText={() => {}}
            placeholder="No clear button (onClear omitted)"
          />
          <MoonSearchInput
            value="Returned an error"
            onChangeText={() => {}}
            onClear={() => {}}
            error
          />
          <MoonSearchInput value="Disabled" onChangeText={() => {}} disabled />
        </Section>

        <Section
          title="MoonDetailRow"
          description="Read-only label-above-value display row for detail screens. Distinct from MoonSettingRow (which is the tappable nav variant)."
        >
          <View
            style={{
              borderRadius: 12,
              backgroundColor: "rgba(255,255,255,0.5)",
              padding: spacing.md,
              gap: spacing.md,
            }}
          >
            <MoonDetailRow
              icon={MapPin}
              label="Location"
              value="Cornell IVF Center · NYC"
            />
            <MoonDetailRow
              icon={Calendar}
              label="Deadline"
              value="December 31, 2026"
            />
            <MoonDetailRow
              icon={Building2}
              label="Frequency"
              value="Open year-round"
            />
            <MoonDetailRow
              label="No icon"
              value="Renders without the leading icon column."
            />
            <MoonDetailRow
              icon={Calendar}
              label="Custom value color"
              value="Coral accent for emphasis"
              valueColor={colors.coral}
            />
          </View>
        </Section>

        <Section
          title="Interaction states (phase 2.1a)"
          description="hover/focus/pressed/disabled deltas applied uniformly across interactive primitives. On web, mouse hover dims to opacity 0.85, keyboard focus shows a warm 2px outline."
        >
          <MoonText variant="body-sm" color="textMid">
            Try with mouse + keyboard on web:
          </MoonText>
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              gap: spacing.md,
            }}
          >
            <MoonPressable
              onPress={() => {}}
              style={{
                paddingHorizontal: 14,
                paddingVertical: 10,
                borderRadius: 12,
                backgroundColor: colors.warm,
              }}
            >
              <MoonText style={{ color: "#FFFFFF", fontWeight: "600" }}>
                Hover or tab to me
              </MoonText>
            </MoonPressable>
            <MoonPressable
              onPress={() => {}}
              disabled
              style={{
                paddingHorizontal: 14,
                paddingVertical: 10,
                borderRadius: 12,
                backgroundColor: colors.warm,
              }}
            >
              <MoonText style={{ color: "#FFFFFF", fontWeight: "600" }}>
                Disabled
              </MoonText>
            </MoonPressable>
            <MoonPressable
              onPress={() => {}}
              style={{
                paddingHorizontal: 14,
                paddingVertical: 10,
                borderRadius: 12,
                borderWidth: 1,
                borderColor: colors.border,
                backgroundColor: "rgba(255,255,255,0.5)",
              }}
            >
              <MoonText style={{ color: colors.text, fontWeight: "600" }}>
                Secondary
              </MoonText>
            </MoonPressable>
          </View>

          <MoonText
            variant="body-sm"
            color="textMid"
            style={{ marginTop: spacing.md }}
          >
            Static visual reference for each state (no real interaction):
          </MoonText>
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              gap: spacing.md,
              alignItems: "flex-start",
            }}
          >
            <View style={{ alignItems: "center", gap: 6 }}>
              <View
                style={{
                  paddingHorizontal: 14,
                  paddingVertical: 10,
                  borderRadius: 12,
                  backgroundColor: colors.warm,
                }}
              >
                <MoonText style={{ color: "#FFFFFF", fontWeight: "600" }}>
                  Idle
                </MoonText>
              </View>
              <MoonText variant="caption" color="textFaint">
                base
              </MoonText>
            </View>
            <View style={{ alignItems: "center", gap: 6 }}>
              <View
                style={{
                  paddingHorizontal: 14,
                  paddingVertical: 10,
                  borderRadius: 12,
                  backgroundColor: colors.warm,
                  opacity: 0.85,
                }}
              >
                <MoonText style={{ color: "#FFFFFF", fontWeight: "600" }}>
                  Hovered
                </MoonText>
              </View>
              <MoonText variant="caption" color="textFaint">
                opacity 0.85
              </MoonText>
            </View>
            <View style={{ alignItems: "center", gap: 6 }}>
              <View
                style={{
                  paddingHorizontal: 14,
                  paddingVertical: 10,
                  borderRadius: 12,
                  backgroundColor: colors.warm,
                  borderWidth: 2,
                  borderColor: colors.warm,
                  outlineColor: colors.warm,
                  outlineWidth: 2,
                  outlineStyle: "solid",
                  outlineOffset: 2,
                }}
              >
                <MoonText style={{ color: "#FFFFFF", fontWeight: "600" }}>
                  Focused
                </MoonText>
              </View>
              <MoonText variant="caption" color="textFaint">
                warm 2px outline
              </MoonText>
            </View>
            <View style={{ alignItems: "center", gap: 6 }}>
              <View
                style={{
                  paddingHorizontal: 14,
                  paddingVertical: 10,
                  borderRadius: 12,
                  backgroundColor: colors.warm,
                  opacity: 0.7,
                  transform: [{ scale: 0.97 }],
                }}
              >
                <MoonText style={{ color: "#FFFFFF", fontWeight: "600" }}>
                  Pressed
                </MoonText>
              </View>
              <MoonText variant="caption" color="textFaint">
                scale 0.97 + opacity 0.7
              </MoonText>
            </View>
            <View style={{ alignItems: "center", gap: 6 }}>
              <View
                style={{
                  paddingHorizontal: 14,
                  paddingVertical: 10,
                  borderRadius: 12,
                  backgroundColor: colors.warm,
                  opacity: 0.4,
                }}
              >
                <MoonText style={{ color: "#FFFFFF", fontWeight: "600" }}>
                  Disabled
                </MoonText>
              </View>
              <MoonText variant="caption" color="textFaint">
                opacity 0.4
              </MoonText>
            </View>
          </View>
        </Section>

        <Section
          title="Disabled opacity audit"
          description="Side-by-side comparison of disabled opacity across primitives. Some use 0.5, some 0.4, MoonSettingRow (no onPress) applies none."
        >
          <MoonText variant="body-sm" color="textMid">
            opacity 0.5 — Checkbox, Radio, Stepper, Toggle:
          </MoonText>
          <MoonCheckbox
            value
            onChange={() => {}}
            label="Checkbox (disabled)"
            disabled
          />
          <MoonRadio
            value
            onChange={() => {}}
            label="Radio (disabled)"
            disabled
          />
          <MoonStepper value={3} onChange={() => {}} disabled />
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <MoonText variant="body-sm" color="textMid">
              Toggle (disabled)
            </MoonText>
            <MoonToggle value onChange={() => {}} disabled />
          </View>

          <MoonText
            variant="body-sm"
            color="textMid"
            style={{ marginTop: spacing.md }}
          >
            opacity 0.4 — MoonPressable, DialogPrimaryButton:
          </MoonText>
          <View style={{ flexDirection: "row", gap: spacing.md }}>
            <MoonPressable
              onPress={() => {}}
              disabled
              style={{
                paddingHorizontal: 14,
                paddingVertical: 10,
                borderRadius: 12,
                backgroundColor: colors.warm,
              }}
            >
              <MoonText style={{ color: "#FFFFFF", fontWeight: "600" }}>
                Pressable
              </MoonText>
            </MoonPressable>
            <DialogPrimaryButton
              label="Primary btn"
              onPress={() => {}}
              disabled
            />
          </View>

          <MoonText
            variant="body-sm"
            color="textMid"
            style={{ marginTop: spacing.md }}
          >
            opacity 0.7 — FilterChip:
          </MoonText>
          <View style={{ flexDirection: "row", gap: 8 }}>
            <MoonFilterChip
              label="Chip (disabled)"
              selected
              onPress={() => {}}
              disabled
            />
            <MoonFilterChip
              label="Chip idle (disabled)"
              selected={false}
              onPress={() => {}}
              disabled
            />
          </View>

          <MoonText
            variant="body-sm"
            color="textMid"
            style={{ marginTop: spacing.md }}
          >
            MoonSettingRow — normal / info-only / disabled:
          </MoonText>
          <View
            style={{
              borderRadius: 12,
              backgroundColor: "rgba(255,255,255,0.5)",
              overflow: "hidden",
            }}
          >
            <MoonSettingRow
              label="Normal"
              value="Tappable"
              onPress={() => {}}
            />
            <MoonSettingRow label="Info only" value="No onPress" divider />
            <MoonSettingRow
              label="Disabled"
              value="onPress + disabled"
              onPress={() => {}}
              disabled
              divider
            />
          </View>
        </Section>

        <View style={{ marginTop: spacing.xl, marginBottom: spacing.lg }}>
          <MoonText variant="caption" color="textFaint">
            Live values · count={String(count)} · cost={String(cost)} · time=
            {time || "(empty)"} · frequency={String(frequency)} · date=
            {date || "(empty)"} · phone={phone || "(empty)"} · steps=
            {String(steps)} · toggle={String(toggleOn)} · radio={radio} · chips=
            {Object.entries(chipSelections)
              .filter(([, v]) => v)
              .map(([k]) => k)
              .join(",") || "(none)"}{" "}
            · checkboxes=
            {Object.entries(checkboxes)
              .filter(([, v]) => v)
              .map(([k]) => k)
              .join(",") || "(none)"}{" "}
            · search={search || "(empty)"}
          </MoonText>
        </View>
      </ScrollView>
    </GlassScreenLayout>
  );
}
